import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import HospitalCommandTabs from "@/components/hospital/CommandTabs";

const CATEGORIES = ["patient_safety", "medication", "hygiene", "staff", "equipment", "other"];
const SEVERITIES = ["low", "medium", "high", "critical"];

export default function HospitalIncidents() {
  const [incidents, setIncidents] = useState([]);
  const [hospital, setHospital] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "patient_safety", severity: "low", description: "" });

  const load = async () => {
    const [h, i] = await Promise.all([api.get("/hospital/me"), api.get("/hospital/incidents")]);
    setHospital(h.data);
    setIncidents(i.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!hospital) return;
    if (!form.description) { toast.error("Add a description"); return; }
    try {
      await api.post("/hospital/incidents", { ...form, hospital_id: hospital.id });
      toast.success("Incident recorded");
      setOpen(false);
      setForm({ category: "patient_safety", severity: "low", description: "" });
      load();
    } catch {
      toast.error("Could not record incident");
    }
  };

  const setStatus = async (id, status) => {
    await api.put(`/hospital/incidents/${id}`, { status });
    load();
  };

  return (
    <AppShell
      pageTitle="Incident management"
      pageDescription="Log, investigate, and resolve patient-safety and operational incidents."
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-incident-button"><Plus size={14} className="mr-1.5" /> New incident</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Log an incident</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="incident-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="incident-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="incident-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="incident-save-button">Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <HospitalCommandTabs />
      {incidents.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <AlertTriangle size={28} className="mx-auto text-muted-foreground" />
          <div className="mt-3 font-serif text-xl text-primary">No incidents recorded</div>
          <p className="text-sm text-muted-foreground mt-1">Log your first incident to start building the safety timeline.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="incidents-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Reported</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((i) => (
                <tr key={i.id} className="border-t border-border" data-testid={`incident-row-${i.id}`}>
                  <td className="px-4 py-3 capitalize text-primary font-medium">{i.category.replace("_", " ")}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${severityStyle(i.severity)}`}>
                      {i.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{i.description}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(i.created_at)}</td>
                  <td className="px-4 py-3">
                    <Select value={i.status} onValueChange={(v) => setStatus(i.id, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid={`incident-status-select-${i.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="investigating">Investigating</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}

function severityStyle(sev) {
  switch (sev) {
    case "critical": return "bg-destructive/12 text-destructive";
    case "high": return "bg-accent/15 text-accent";
    case "medium": return "bg-[#EAB308]/15 text-[#854D0E]";
    default: return "bg-secondary/12 text-secondary";
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { dateStyle: "medium" }); }
  catch { return iso; }
}
